import customerlocationService from '../../../services/customerlocationService';
import filterService from '../../../services/filterService';

import {
    setCustomerlocationLoading,
    setCustomerlocationError,
    setCustomerlocationList,
    setCustomerlocationCurrent,
    setCustomerlocationPagination,
    setCustomerlocationSort,
    setCustomerlocationFilters,
    setCustomerlocationDefaultFilter,
    setCustomerlocationSettings,
    clearCustomerlocationCurrent,
    clearCustomerlocationError,
    setCustomerlocationFilterLoading,
    changeCustomerlocationFilterCondition,
    addCustomerlocationFilterCondition,
    updateCustomerlocationFilterConditionRule,
    updateCustomerlocationFilterConditionValue,
    removeCustomerlocationFilterCondition,
    setCustomerlocationSelected,
    setCustomerlocationDownloading,
    resetCustomerlocationFiltersAndPagination,

    setCustomerlocationClassicFilters
} from './customerlocationSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().customerlocation.isSettingsInitialized) {
            dispatch(setCustomerlocationLoading(true));
            const response = await customerlocationService.getSettings();

            // Set the settings
            dispatch(setCustomerlocationSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setCustomerlocationClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setCustomerlocationDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setCustomerlocationError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setCustomerlocationLoading(false));
    }
};

export const fetchCustomerlocations = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().customerlocation;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setCustomerlocationLoading(true));
        const response = await customerlocationService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setCustomerlocationList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchCustomerlocations:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setCustomerlocationError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCustomerlocationLoading(false));
    }
};

export const fetchCustomerlocation = (id) => async (dispatch) => {
    try {
        dispatch(setCustomerlocationLoading(true));
        const response = await customerlocationService.getOne(id);
        dispatch(setCustomerlocationCurrent(response.data));
    } catch (error) {
        dispatch(setCustomerlocationError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setCustomerlocationLoading(false));
    }
};

export const createCustomerlocation = (data) => async (dispatch) => {
    try {
        const response = await customerlocationService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateCustomerlocation = (id, data) => async (dispatch) => {
    try {
        const response = await customerlocationService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteCustomerlocation = (id) => async (dispatch) => {
    try {
        dispatch(setCustomerlocationLoading(true));
        const response = await customerlocationService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setCustomerlocationError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCustomerlocationLoading(false));
    }
};

export const downloadCustomerlocation = (type) => async (dispatch, getState) => {
    try {
        const state = getState().customerlocation;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setCustomerlocationDownloading(true));
        const response = await customerlocationService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setCustomerlocationDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading customerlocations:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setCustomerlocationError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCustomerlocationDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setCustomerlocationPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setCustomerlocationSort(sort));
};

export const searchCustomerlocations = (query) => (dispatch, getState) => {
    const currentFilters = getState().customerlocation.filters;
    dispatch(setCustomerlocationFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setCustomerlocationDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeCustomerlocationFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addCustomerlocationFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateCustomerlocationFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateCustomerlocationFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeCustomerlocationFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setCustomerlocationFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().customerlocation.settings;
            dispatch(setCustomerlocationSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setCustomerlocationFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setCustomerlocationFilterLoading(false));
        dispatch(setCustomerlocationError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setCustomerlocationSelected(selectedRows));
    };
};

export const clearCurrentCustomerlocation = () => {
    return (dispatch) => {
        dispatch(clearCustomerlocationCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetCustomerlocationFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().customerlocation.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setCustomerlocationClassicFilters(updatedClassicFilters));
};